import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Check, ExternalLink, HelpCircle, Quote, TrendingUp } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo, organizationSchema } from "@/lib/seo";
import { Reveal, MaskReveal, stagger, item } from "@/components/Reveal";
import { EcosystemCore } from "@/components/EcosystemCore";
import { RoiCalculator } from "@/components/RoiCalculator";
import { DynIcon } from "@/components/DynIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  VALUE_HIGHLIGHTS,
  VALUE_BADGES,
  TRUST_METRICS,
  PRODUCTS,
  STORIES,
  FAQS,
} from "@/lib/mockData";

const financialServiceSchema = {
  "@context": "https://schema.org",
  "@type": "FinancialService",
  name: "Elikya Technology",
  description:
    "Elikya Technology provides secure, reliable payment solutions that help merchants accept money, automate billing and scale across emerging and global markets.",
  areaServed: ["South Africa", "Democratic Republic of the Congo"],
  provider: { "@type": "Organization", name: "Elikya Technology, Inc." },
};

const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Elikya Technology Product Suite",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, Cloud",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Start processing payments for free" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", ratingCount: "512" },
  description:
    "Girdercore, NightOwl and Zando — the proprietary products built by Elikya Technology.",
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <Layout>
      <Seo
        title="Powering Modern Payments. Unlocking Growth Everywhere."
        description="Elikya Technology provides the secure, reliable payment foundation that helps merchants accept money, automate billing and scale across emerging and global markets."
        keywords="elikya, elikya technology, elikya tech, fintech africa, payments africa, cross-border payments africa, pan-african payment infrastructure, payments south africa, payments congo, payments drc, kinshasa payment gateway, mobile money integration hub, merchant payment gateway africa, financial ledger engine, girdercore, zando, ecommerce africa"
        path="/"
        jsonLd={[organizationSchema, financialServiceSchema, softwareSchema]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div style={{ y: gridY }} className="pointer-events-none absolute inset-0 grid-lines opacity-70" />
        <div className="pointer-events-none absolute -top-24 right-[-10%] h-[520px] w-[520px] rounded-full bg-brand-tint blur-3xl" />
        <div className="pointer-events-none absolute top-44 left-[-12%] h-[360px] w-[360px] rounded-full bg-brand-tint blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-16 lg:pt-24 lg:pb-24">
          <div className="grid items-center gap-14 lg:grid-cols-[1fr_1fr]">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-xs font-semibold text-emerald-700"
              >
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-blip" /> 100% Active Rails
              </motion.div>

              <h1 className="mt-6 text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-[3.6rem]">
                <MaskReveal
                  lines={["Powering Modern Payments.", "Unlocking Growth", "Everywhere."]}
                  lineClassName="text-gradient-brand"
                />
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
                className="mt-6 max-w-xl text-base leading-relaxed text-slatey sm:text-lg"
              >
                Elikya Technology provides the secure, reliable payment foundation that helps merchants
                accept money, automate billing and scale across emerging and global markets.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.68 }}
                className="mt-7 flex flex-wrap items-center gap-2.5"
              >
                {VALUE_BADGES.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-tint px-3 py-1.5 text-xs font-semibold text-brand"
                  >
                    <Check className="h-3.5 w-3.5" /> {b}
                  </span>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link
                  to="/signup"
                  data-testid="hero-get-started"
                  className="group inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-[0.95rem] font-semibold text-white shadow-float transition-all duration-200 hover:bg-brand-hover"
                >
                  Get Started
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-explore-solutions"
                  className="inline-flex items-center gap-2 rounded-xl border border-stroke bg-white px-5 py-3 text-[0.95rem] font-semibold text-ink transition-all duration-200 hover:bg-brand-tint"
                >
                  Explore Solutions
                </Link>
              </motion.div>
            </div>

            {/* Interactive ecosystem core */}
            <motion.div
              style={{ y: yVisual }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <EcosystemCore />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST & METRICS STRIP */}
      <section aria-label="Proof metrics" className="border-y border-stroke bg-white py-10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-center text-sm font-medium text-slatey">
            Processing over <span className="font-bold text-ink">$100M+</span> in annualized volume across{" "}
            <span className="font-bold text-ink">10,000+</span> growth businesses
          </p>
          <div className="mt-6 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {TRUST_METRICS.map((m) => (
              <div key={m.v} className="text-center">
                <p className="text-3xl font-bold tracking-tight text-brand tabular-nums">{m.k}</p>
                <p className="mt-1 text-xs text-slatey">{m.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-24">
        <Reveal>
          <RoiCalculator />
        </Reveal>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="scroll-mt-24 mx-auto max-w-7xl px-5 sm:px-8 pb-20 lg:pb-28">
        <Reveal className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
            <Sparkles className="h-4 w-4" /> Why businesses choose Elikya
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Everything you need to get paid — and keep growing
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slatey">
            From a single corner shop to a continent-spanning marketplace, Elikya removes the friction
            between your customer and their payment.
          </p>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {VALUE_HIGHLIGHTS.map((v) => (
            <motion.article
              key={v.title}
              variants={item}
              data-testid={`value-card-${v.title.split(" ")[0].toLowerCase()}`}
              className="group rounded-2xl border border-stroke bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-float"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-tint text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <DynIcon name={v.icon} className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-ink">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slatey">{v.desc}</p>
              <p className="mt-4 inline-flex items-center rounded-lg bg-canvas px-2.5 py-1 text-xs font-semibold text-brand">
                {v.metric}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* PRODUCTS SUITE */}
      <section id="product" className="scroll-mt-24 border-t border-stroke bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="text-sm font-semibold text-brand">Built by Elikya Technology</span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              A suite of products engineered to work as one
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slatey">
              Three proprietary products, each solving a different piece of the modern money stack.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`product-card-${p.name.toLowerCase()}`}
                  className="group flex h-full flex-col rounded-2xl border border-stroke bg-canvas p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:bg-white hover:shadow-float"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white shadow-float">
                      <DynIcon name={p.icon} className="h-5.5 w-5.5" />
                    </div>
                    <ExternalLink className="h-5 w-5 text-slatey transition-all group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-ink">{p.name}</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand">{p.tag}</p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slatey">{p.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                    Visit {p.url.replace("https://", "")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER STORIES */}
      <section className="border-t border-stroke bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
              <Quote className="h-4 w-4" /> Customer stories
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Real businesses, real before &amp; after
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slatey">
              See how merchants transformed the way they get paid after switching to Elikya.
            </p>
          </Reveal>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-12 grid gap-5 lg:grid-cols-3"
          >
            {STORIES.map((s) => (
              <motion.article
                key={s.name}
                variants={item}
                data-testid={`story-${s.name.split(" ")[0].toLowerCase()}`}
                className="flex h-full flex-col rounded-2xl border border-stroke bg-canvas p-7 transition-all duration-300 hover:border-brand/40 hover:bg-white hover:shadow-float"
              >
                <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-brand-tint px-3 py-1 text-sm font-bold text-brand">
                  <TrendingUp className="h-4 w-4" /> {s.metric}
                </span>
                <Quote className="mt-5 h-6 w-6 text-brand/30" aria-hidden="true" />
                <p className="mt-2 flex-1 text-[0.95rem] leading-relaxed text-ink">“{s.quote}”</p>

                <div className="mt-6 space-y-2 rounded-xl border border-stroke bg-white p-4">
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 w-12 shrink-0 text-[0.65rem] font-bold uppercase tracking-wider text-slatey">Before</span>
                    <span className="text-xs text-slatey line-through decoration-slatey/40">{s.before}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 w-12 shrink-0 text-[0.65rem] font-bold uppercase tracking-wider text-brand">After</span>
                    <span className="text-xs font-semibold text-ink">{s.after}</span>
                  </div>
                </div>

                <div className="mt-5 border-t border-stroke pt-4">
                  <p className="text-sm font-semibold text-ink">{s.name}</p>
                  <p className="text-xs text-slatey">{s.type}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-20 lg:py-24">
        <Reveal className="text-center">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
            <HelpCircle className="h-4 w-4" /> Frequently asked questions
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Answers for growing businesses
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="rounded-2xl border border-stroke bg-white px-5 shadow-sm"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-ink hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slatey">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* CONVERSION BANNER */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-brand/20 bg-brand px-8 py-14 text-center shadow-float-lg sm:px-16">
            <div className="pointer-events-none absolute inset-0 grid-dots opacity-30" />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Start processing payments today
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base text-white/85">
                Join the businesses accepting money, automating billing and settling instantly with Elikya.
                No complex setup — go live in minutes.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to="/signup"
                  data-testid="banner-create-account"
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-[0.95rem] font-semibold text-brand transition-transform hover:scale-[1.02]"
                >
                  Create Business Account <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-[0.95rem] font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Talk to our team
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
};

export default Home;
